import * as React from 'react'
import moment from 'moment'
import { AlertModel } from '@/app/models/alert/alertModel'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { TableHeaderColumn } from '@/modules/drivers/types'
import { TIME_FORMAT } from '@/app/constants/date'
import { getSingleAlertName } from '@/app/common/converters/alert/singleAlertTypeToTextConverter'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'

// Treść alertu przychodzi z serwera z datą w formacie MM/DD/YYYY (kultura en-US).
// Ujednolicamy ją do DD-MM-YYYY, spójnie z kolumną "Czas zdarzenia" (TIME_FORMAT).
const normalizeAlertDates = (text: string): string =>
  text.replace(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/g, (match, month, day, year) => {
    const m = Number(month)
    const d = Number(day)
    if (m < 1 || m > 12 || d < 1 || d > 31) return match
    return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`
  })

const AlertsTableColumns: TableHeaderColumn[] = [
  // {
  //   title: 'alerts.visible',
  //   key: 'visible',
  //   type: 'string',
  //   sort: false,
  // },
  {
    title: 'alerts.eventTime',
    key: 'eventTime',
    type: 'date',
    sort: true,
  },
  {
    title: 'alerts.type',
    key: 'type',
    type: 'string',
    sort: true,
  },
  {
    title: 'alerts.object',
    key: 'object',
    type: 'string',
    sort: true,
  },
  {
    title: 'alerts.text',
    key: 'text',
    type: 'string',
    sort: true,
  },
  // {
  //   title: '',
  //   key: 'edit',
  //   type: 'string',
  //   sort: true,
  // },
]

type AlertsTableRowProps = {
  alert: AlertModel
  isChecked: boolean
  onToggle: (checked: boolean) => void
  onEdit: () => void
  canEdit: boolean
  upcoming: boolean
}

const AlertsTableRow: React.FC<AlertsTableRowProps> = ({
  alert,
  isChecked,
  onToggle,
  onEdit,
  canEdit,
  upcoming,
}) => {
  const { t } = useLocalize()

  const [isHovered, setIsHovered] = React.useState(false)

  return (
    <tr
      className={`notches-border--right tw-w-fit tw-cursor-pointer tw-border-b tw-bg-white hover:tw-bg-primary/10 ${isChecked ? 'tw-bg-primary/10 tw-font-medium' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onToggle(!isChecked)}

      //   if (canEdit) {
      //     onEdit()
      //   }
    >
      <TableCheckbox
        checked={isChecked}
        onChange={onToggle}
        disabled={false}
        isHovered={isHovered}
      />

      <TableTextRow size="medium" bolder={false}>
        {moment(alert.eventTime).format(TIME_FORMAT)}
      </TableTextRow>
      <TableTextRow size="medium" bolder={false}>
        {getSingleAlertName(alert.type, t)}
      </TableTextRow>
      <TableTextRow size="medium" bolder={false}>
        {alert.object || '-'}
      </TableTextRow>
      <TableTextRow size="medium" bolder={false}>
        {alert.text ? normalizeAlertDates(alert.text) : ''}
      </TableTextRow>
      {/* <td
        className={`tw-cursor-pointer tw-px-3 tw-py-3.5 ${!canEdit ? 'tw-opacity-30' : ''}`}
        onClick={(e) => {
          e.stopPropagation()
          if (canEdit) {
            onEdit()
          }
        }}
      >
        {canEdit ? (
          <img
            width={18}
            height={18}
            alt={t('operation.edit')}
            title={t('operation.edit')}
            className="tw-opacity-70 hover:tw-opacity-100"
          />
        ) : (
          <div className="tw-h-[18px] tw-w-[18px]" />
        )}
      </td> */}
    </tr>
  )
}

type AlertsTableProps = {
  alerts: AlertModel[]
  selectedAlerts: number[]
  onSelectAlert: (index: number, checked: boolean) => void
  onSelectAll: (checked: boolean) => void
  onEdit: (alert: AlertModel) => void
  canEdit: boolean
  upcoming: boolean
}

export const AlertsTable: React.FC<AlertsTableProps> = ({
  alerts,
  selectedAlerts,
  onSelectAlert,
  onSelectAll,
  onEdit,
  canEdit,
  upcoming,
}) => {
  const { t } = useLocalize()
  const allSelected =
    alerts.length > 0 && selectedAlerts.length === alerts.length
  const isSelectedAny = selectedAlerts.length > 0

  const toggleAll = (checked: boolean, isActive: boolean) => {
    onSelectAll(checked)
  }

  // Translate column titles
  const translatedColumns = React.useMemo(() => {
    return AlertsTableColumns.map((col) => ({
      ...col,
      title: col.title ? t(col.title) : '',
    }))
  }, [t])
  const { sortedData, sortConfig, handleHeaderClick, setSortConfig } =
    useSortableData({
      data: alerts,
      tableColumns: AlertsTableColumns,
    })

  React.useEffect(() => {
    setSortConfig({ key: 'eventTime', direction: 'asc' })
  }, [alerts])

  if (alerts.length === 0) {
    return (
      <div className="tw-rounded-lg tw-p-8 tw-text-center">
        <p className="tw-text-content-base tw-opacity-70">
          {t('alerts.empty-results')}
        </p>
      </div>
    )
  }

  return (
    <div className="tw-h-full tw-overflow-y-auto">
      <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
        <TableHeader
          columns={translatedColumns}
          fontSize="md"
          sticky
          isSelectedAny={allSelected}
          rounded={false}
          sortConfig={sortConfig}
          bolder
          opacity
          disabledToggle={false}
          toggleAll={toggleAll}
          onHeaderClick={handleHeaderClick}
        />
        <tbody>
          {sortedData.map((alert) => {
            const isChecked = selectedAlerts.includes(alert.index ?? -1)
            return (
              <AlertsTableRow
                key={`${alert.alertId}-${upcoming ? 'up' : 'out'}-${alert.index}`}
                alert={alert}
                isChecked={isChecked}
                onToggle={(checked) =>
                  onSelectAlert(alert.index ?? -1, checked)
                }
                onEdit={() => onEdit(alert)}
                canEdit={canEdit}
                upcoming={upcoming}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
